import {
  ChevronDownIcon,
  ChevronRightIcon,
  DocumentTextIcon,
  EllipsisHorizontalIcon,
  LockClosedIcon,
} from '@heroicons/react/20/solid';
import * as React from 'react';

import { MarkdownText } from '@/components/common/MarkdownText';
import { UserLink } from '@/components/entity-links';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { usePushChanges } from '@/contexts/PushChangesContext';
import {
  enhancedApi as externalTaskApi,
  type ExternalReplySchema,
  type ExternalTaskWithLinkSchema,
} from '@/services/api/generated/externalTaskService';
import {
  TaskUpdate,
  TaskUpdateVisibility,
} from '@/services/types/client-intake-types';

import { TaskUpdateEditor } from '../TaskUpdateEditor';
import {
  formatUpdateDate,
  getUpdateAuthorName,
  isTaskUpdateUser,
} from '../taskUpdateUtils';
import { isSystemNote } from './types';

interface TimelineUpdateProps {
  update: TaskUpdate;
  taskId: string;
  linkedExternalTasks: ExternalTaskWithLinkSchema[];
  onEdit: (update: TaskUpdate) => void;
  onDelete: (updateId: string) => void;
  isEditing: boolean;
  onCancelEdit: () => void;
  onSaveEdit: (content: string) => Promise<void>;
  collapseSystemMessages?: boolean;
}

interface ReplyMatch {
  externalTaskId: string;
  externalTaskName: string;
  replyId: string;
  snippet: string;
  createdAt: string | null;
}

const MIN_MATCH_CHARS = 24;

function normalizeForComparison(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function truncateSnippet(value: string, maxChars = 220): string {
  if (value.length <= maxChars) {
    return value;
  }
  return `${value.slice(0, maxChars)}...`;
}

function hasLikelyReplyMatch(updateText: string, replyText: string): boolean {
  const normalizedUpdate = normalizeForComparison(updateText);
  const normalizedReply = normalizeForComparison(replyText);

  if (!normalizedUpdate || !normalizedReply) {
    return false;
  }

  if (normalizedUpdate === normalizedReply) {
    return true;
  }

  if (
    normalizedUpdate.length >= MIN_MATCH_CHARS &&
    normalizedReply.includes(normalizedUpdate)
  ) {
    return true;
  }

  const updateTokens = new Set(
    normalizedUpdate.split(' ').filter((token) => token.length >= 3),
  );
  if (updateTokens.size < 6) {
    return false;
  }

  const replyTokens = new Set(
    normalizedReply.split(' ').filter((token) => token.length >= 3),
  );
  let overlapCount = 0;
  updateTokens.forEach((token) => {
    if (replyTokens.has(token)) {
      overlapCount += 1;
    }
  });

  return overlapCount / updateTokens.size >= 0.85;
}

/**
 * Renders a standalone user comment in the task timeline.
 *
 * This component displays comments that are NOT linked to a domain event
 * (i.e., comments without a correlation_id, or whose correlation_id doesn't
 * match any event). For comments linked to field changes, see TimelineItemCorrelated.
 *
 * Features:
 * - User name display
 * - Timestamp formatting
 * - Private/public visibility indicator
 * - Edit and delete actions (via dropdown menu)
 * - Inline editing mode
 * - Attachment display
 */
export const TimelineItemComment: React.FC<TimelineUpdateProps> = ({
  update,
  taskId,
  linkedExternalTasks,
  onEdit,
  onDelete,
  isEditing,
  onCancelEdit,
  onSaveEdit,
  collapseSystemMessages = false,
}) => {
  const isSystem = isSystemNote(update);
  const [isCollapsed, setIsCollapsed] = React.useState(collapseSystemMessages && isSystem);

  const { pushChanges } = usePushChanges();
  const [fetchExternalTaskDetail] = externalTaskApi.useLazyExternalTaskRetrieveQuery();
  const hasLinkedFilevineTask = React.useMemo(
    () => linkedExternalTasks.some((task) => task.source === 'FILEVINE'),
    [linkedExternalTasks],
  );
  const [isCheckingMatches, setIsCheckingMatches] = React.useState(false);
  const [isExceptionOpen, setIsExceptionOpen] = React.useState(false);
  const [replyMatches, setReplyMatches] = React.useState<ReplyMatch[]>([]);

  const pushToFilevine = async () => {
    await pushChanges({
      internalTaskId: taskId,
      sources: ['FILEVINE'],
      changes: [
        {
          changeType: 'comment',
          changeData: {
            comment: update.update,
            source_task_update_id: update.id,
          },
        },
      ],
    });
  };

  const findReplyMatches = async (): Promise<ReplyMatch[]> => {
    const filevineTasks = linkedExternalTasks.filter(
      (externalTask) => externalTask.source === 'FILEVINE',
    );
    if (!filevineTasks.length) {
      return [];
    }

    const taskDetails = await Promise.all(
      filevineTasks.map(async (externalTask) => {
        const detail = await fetchExternalTaskDetail({
          id: externalTask.id,
        }).unwrap();
        return {
          taskId: String(externalTask.id),
          taskName: externalTask.name,
          replies: detail.replies,
        };
      }),
    );

    const matches: ReplyMatch[] = [];
    taskDetails.forEach((taskDetail) => {
      taskDetail.replies.forEach((reply: ExternalReplySchema) => {
        if (reply.source_task_update_id && String(reply.source_task_update_id) === update.id) {
          matches.push({
            externalTaskId: taskDetail.taskId,
            externalTaskName: taskDetail.taskName,
            replyId: String(reply.id),
            snippet: truncateSnippet(reply.content),
            createdAt: reply.external_created_at,
          });
          return;
        }

        if (!hasLikelyReplyMatch(update.update, reply.content)) {
          return;
        }

        matches.push({
          externalTaskId: taskDetail.taskId,
          externalTaskName: taskDetail.taskName,
          replyId: String(reply.id),
          snippet: truncateSnippet(reply.content),
          createdAt: reply.external_created_at,
        });
      });
    });

    return matches;
  };

  const handlePushToFilevine = async () => {
    setIsCheckingMatches(true);
    try {
      const matches = await findReplyMatches();
      setReplyMatches(matches);
      if (matches.length > 0) {
        setIsExceptionOpen(true);
        return;
      }
      await pushToFilevine();
    } finally {
      setIsCheckingMatches(false);
    }
  };

  if (isCollapsed && isSystem) {
    const previewText = update.update.length > 80 ? `${update.update.slice(0, 80)}...` : update.update;
    return (
      <div id={`update-${update.id}`} className="group border-b border-slate-100 py-1.5 px-2 hover:bg-slate-50 dark:border-zinc-800 dark:hover:bg-zinc-800/50 transition-colors">
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setIsCollapsed(false)} className="flex items-center gap-1 text-slate-500 hover:text-slate-700 dark:text-zinc-400 dark:hover:text-zinc-200 transition-colors shrink-0">
            <ChevronRightIcon className="size-3.5" />
          </button>
          <span className="text-xs font-semibold text-slate-500 dark:text-zinc-400">{getUpdateAuthorName(update)}</span>
          <span className="text-slate-400 dark:text-zinc-500">·</span>
          <span className="text-xs text-slate-400 dark:text-zinc-500">{formatUpdateDate(update.created_at)}</span>
          <span className="text-xs text-slate-500 dark:text-zinc-400 truncate">— {previewText}</span>
        </div>
      </div>
    );
  }

  return (
    <div
      id={`update-${update.id}`}
      className={`group border-b border-slate-100 py-2 px-2 hover:bg-slate-50 dark:border-zinc-800 dark:hover:bg-zinc-800/50 transition-colors ${
        isEditing ? 'relative z-10' : ''
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          {isSystem && collapseSystemMessages && (
            <button type="button" onClick={() => setIsCollapsed(true)} className="text-slate-500 hover:text-slate-700 dark:text-zinc-400 dark:hover:text-zinc-200 transition-colors shrink-0">
              <ChevronDownIcon className="size-3.5" />
            </button>
          )}
          {isTaskUpdateUser(update.user) ? (
            <UserLink
              entityId={update.user.id}
              className="text-xs font-semibold text-slate-900 dark:text-zinc-100 leading-none
                       underline decoration-dotted decoration-slate-300 dark:decoration-zinc-600 underline-offset-2
                       hover:text-blue-600 hover:decoration-blue-400 hover:decoration-solid"
            >
              {getUpdateAuthorName(update)}
            </UserLink>
          ) : (
            <span className="text-xs font-semibold text-slate-900 dark:text-zinc-100">
              {getUpdateAuthorName(update)}
            </span>
          )}
          <span className="text-slate-400 dark:text-zinc-500">·</span>
          <span className="text-xs text-slate-400 dark:text-zinc-500">
            {formatUpdateDate(update.created_at)}
          </span>
          {update.visibility === TaskUpdateVisibility.PRIVATE && (
            <LockClosedIcon
              className="size-3 text-slate-300 dark:text-zinc-600"
              title="Private to staff"
            />
          )}
        </div>

        {/* Actions Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
              className="p-1 rounded-md text-slate-300 hover:text-slate-600 hover:bg-slate-100 dark:text-zinc-600 dark:hover:text-zinc-300 dark:hover:bg-zinc-700 opacity-0 group-hover:opacity-100 transition-all"
            >
              <EllipsisHorizontalIcon className="size-4" />
            </button>
          </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 z-50">
              <DropdownMenuItem
                onSelect={() => {
                  handlePushToFilevine().catch(() => {});
                }}
                disabled={!hasLinkedFilevineTask || isCheckingMatches}
                className="text-xs"
              >
                Push to Filevine
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => onEdit(update)}
                className="text-xs"
            >
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => onDelete(update.id)}
              className="text-xs text-red-600"
            >
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Content */}
      {isEditing ? (
        <div className="mt-1">
          <TaskUpdateEditor
            initialContent={update.update}
            onSave={onSaveEdit}
            onCancel={onCancelEdit}
          />
        </div>
      ) : (
        <p className="mt-1 text-xs font-medium text-slate-600 dark:text-zinc-300">
          <MarkdownText content={update.update} className="inline" />
        </p>
      )}

      {/* Attachments */}
      {update.attachments.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {update.attachments.map((attachment) => (
            <div
              key={attachment.id}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-slate-50 border border-slate-100 text-xs text-slate-600 hover:bg-slate-100 dark:bg-zinc-800 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-700 transition-colors cursor-pointer"
            >
              <DocumentTextIcon className="size-3.5 text-slate-400 dark:text-zinc-500" />
              <span className="truncate max-w-[200px]">{attachment.name}</span>
            </div>
          ))}
        </div>
      )}

      <Dialog open={isExceptionOpen} onOpenChange={setIsExceptionOpen}>
        <DialogContent className="sm:max-w-[680px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Possible existing external reply match</DialogTitle>
            <DialogDescription>
              We found potential matches in linked Filevine task replies for this update.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {replyMatches.map((match) => (
              <div
                key={`${match.externalTaskId}-${match.replyId}`}
                className="rounded-md border border-amber-200 bg-amber-50 p-3"
              >
                <div className="text-sm font-medium">{match.externalTaskName}</div>
                <div className="text-xs opacity-70 mb-2">
                  {match.createdAt
                    ? new Date(match.createdAt).toLocaleString()
                    : 'Unknown reply timestamp'}
                </div>
                <div className="text-sm whitespace-pre-wrap">{match.snippet}</div>
              </div>
            ))}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsExceptionOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                setIsExceptionOpen(false);
                pushToFilevine().catch(() => {});
              }}
            >
              Push Anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
